import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listChallengeTemplates, listMyChallenges, enrollChallenge, checkInChallenge } from "@/lib/journey.functions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Flame, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/journey/challenges")({
  component: ChallengesPage,
});

type Template = Awaited<ReturnType<typeof listChallengeTemplates>>[number];
type MyState = Awaited<ReturnType<typeof listMyChallenges>>;

function ChallengesPage() {
  const loadTpl = useServerFn(listChallengeTemplates);
  const loadMine = useServerFn(listMyChallenges);
  const enroll = useServerFn(enrollChallenge);
  const check = useServerFn(checkInChallenge);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [mine, setMine] = useState<MyState>({ challenges: [], progress: [] });
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = () => Promise.all([loadTpl().then(setTemplates), loadMine().then(setMine)]).catch(() => {});
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const progressFor = (ucId: string) => mine.progress.filter((p) => p.user_challenge_id === ucId).length;
  const activeById = new Map(mine.challenges.map((c) => [c.template_id, c] as const));

  const handleEnroll = async (tpl: Template) => {
    setBusy(tpl.id);
    try { await enroll({ data: { template_id: tpl.id } }); toast.success(`Enrolled in ${tpl.title}`); await refresh(); }
    catch (e) { toast.error((e as Error).message); }
    finally { setBusy(null); }
  };

  const handleCheck = async (ucId: string, day: number) => {
    setBusy(ucId + day);
    try { await check({ data: { user_challenge_id: ucId, day_number: day } }); toast.success("Day logged"); await refresh(); }
    catch (e) { toast.error((e as Error).message); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-8">
      {mine.challenges.filter((c) => !c.completed_at && !c.abandoned_at).length > 0 && (
        <section>
          <h2 className="mb-3 font-serif text-xl text-gold-luminous">Your active journeys</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {mine.challenges.filter((c) => !c.completed_at && !c.abandoned_at).map((c) => {
              const tpl = c.challenge_templates as { title: string; duration_days: number } | null;
              if (!tpl) return null;
              const done = progressFor(c.id);
              const pct = Math.min(100, Math.round((done / tpl.duration_days) * 100));
              const nextDay = done + 1;
              return (
                <Card key={c.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-serif text-lg text-gold-luminous">{tpl.title}</div>
                      <div className="text-xs text-foreground/60">Day {done} of {tpl.duration_days}</div>
                    </div>
                    <Flame className="text-gold" size={20} />
                  </div>
                  <Progress value={pct} className="mt-3" />
                  <div className="mt-3">
                    <Button
                      size="sm"
                      disabled={nextDay > tpl.duration_days || busy === c.id + nextDay}
                      onClick={() => handleCheck(c.id, nextDay)}
                    >
                      <CheckCircle2 size={14} className="mr-1" />
                      Log day {nextDay}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <h2 className="mb-3 font-serif text-xl text-gold-luminous">Browse challenges</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((tpl) => {
            const active = activeById.get(tpl.id);
            return (
              <Card key={tpl.id} className="flex flex-col p-4">
                <div className="text-xs uppercase tracking-widest text-foreground/50">{tpl.duration_days} days</div>
                <h3 className="mt-1 font-serif text-lg text-gold-luminous">{tpl.title}</h3>
                {tpl.subtitle && <p className="text-sm text-foreground/70">{tpl.subtitle}</p>}
                <p className="mt-2 flex-1 text-sm text-foreground/60">{tpl.description}</p>
                <div className="mt-3">
                  {active ? (
                    <span className="text-xs text-gold">{active.completed_at ? "Completed" : "Enrolled"}</span>
                  ) : (
                    <Button size="sm" onClick={() => handleEnroll(tpl)} disabled={busy === tpl.id}>Enroll</Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      <p className="text-xs text-foreground/50">
        Your reflections along the way live in{" "}
        <Link to="/journey/journal" className="text-gold underline underline-offset-4">your journal</Link>.
      </p>
    </div>
  );
}
